import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ICatalogSelectionRequest, WebshopCatalogSelectionDataService } from './webshop-catalog-selection-data.service';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { GlobalProductDataService, IProductCategory } from '../../globalproduct/global-product-data.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import * as _ from 'underscore';
import { IDialogButton } from '../../../shared/message/ga-message.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { IObject } from '../../../../type_definitions/datatypes';

const ALL_PRODUCT_CATEGORIES: string = 'ALL';
const BUTTON_SAVE: string = 'save';

export interface ICatalogSelectionModel {
  includeInWebshopCatalog: boolean;
  productCategories?: IProductCategory[];
  selectAllCategories: boolean;
}

const VALIDATION_KEY: string = 'productCategoriesOrAllCategories';
const VALIDATION_MESSAGE: string = 'Either select one of product categories or select all product categories';

@Component({
  selector: 'webshop-catalog-selection-dialog',
  template: `
    <default-dialog title="Catalog selection" [buttons]="buttons">
      <formly [form]="form" [model]="model" [fields]="fields"></formly>
    </default-dialog>
  `
})
export class WebshopCatalogSelectionDialogComponent implements OnInit {
  @Input() public webshopId: number;
  @Input() public countryId: number;
  @Input() public companyCountryId: number;
  @Input() public oemId: number;
  @Input() public includeInWebshopCatalog: string;
  @Input() public productCategories: string;
  @Input() public rowSelection: IObject;

  @Output() public onCancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onSaved: EventEmitter<Partial<ICatalogSelectionRequest>> = new EventEmitter<Partial<ICatalogSelectionRequest>>();

  public model: ICatalogSelectionModel;
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fields: IExtraFieldConfigurationObject[];
  public buttons: IDialogButton[];

  private requestData: ICatalogSelectionRequest;
  private productCategoriesField: IExtraFieldConfigurationObject;
  private selectAllCategoriesGroupField: IExtraFieldConfigurationObject;
  private selectAllCategoriesCheckField: IExtraFieldConfigurationObject;
  private preparedRowSelection: IObject[];

  constructor(
    private formlyService: GaFormlyService,
    private globalProductDataService: GlobalProductDataService,
    private formattingService: FormattingService,
    private translationService: TranslationService,
    private webshopCatalogSelectionDataService: WebshopCatalogSelectionDataService,
    private validateService: GaValidateService
  ) {
    /**/
  }

  public ngOnInit(): void {
    // init model
    this.model = {
      includeInWebshopCatalog: this.includeInWebshopCatalog === 'Yes',
      selectAllCategories: this.productCategories === ALL_PRODUCT_CATEGORIES,
      productCategories: []
    };

    this.preparedRowSelection = _.map(this.rowSelection, (row: IObject) => row);
    if (this.preparedRowSelection?.length) {
      this.initModelByRowSelection();
    }

    // create formly fields
    this.fields = this.createFields();

    // trigger onChange for first time
    this.onIncludeSelectionChange(this.model.includeInWebshopCatalog);

    // create dialog buttons
    this.createButtons();

    // fetch list items
    this.fillFields();
  }

  private fillFields(): void {
    this.formlyService.fillField(this.productCategoriesField, this.globalProductDataService.categories())
      .then((categories: IProductCategory[]) => {
        // Value categories_selection is coming from browselist config fallback bindings value
        if (this.productCategories && this.productCategories !== 'Categories_selection') {
          const initCategories: string[] = this.productCategories?.split(', ');

          // add timeout here since data service is using cache then formly might not be fully rendered yet
          setTimeout(() => {
            this.productCategoriesField.formControl.setValue(
              _.map(initCategories, (category: string) => _.findWhere(categories, { description: category }))
            );
          });
        }

        // trigger onChange for this first time
        this.onSelectAllCategoriesChange(this.model.selectAllCategories);
      });
  }

  private onSelectAllCategoriesChange = (selected: boolean): void => {
    this.productCategoriesField.templateOptions.disabled = selected;

    if (this.productCategoriesField?.formControl) {
      this.productCategoriesField.formControl.setValue([]);
      this.productCategoriesField.formControl.updateValueAndValidity();
    }
  };

  private onIncludeSelectionChange = (selected: boolean): void => {
    this.selectAllCategoriesGroupField.hide = !selected;
    this.productCategoriesField.hide = !selected;

    // add timeout here to let angular digest group child fields
    setTimeout(() => {
      this.selectAllCategoriesCheckField.formControl.updateValueAndValidity();
      this.productCategoriesField.formControl.updateValueAndValidity();
    });
  };

  private createButtons(): void {
    this.buttons = [{
      id: BUTTON_SAVE,
      label: this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: 'Catalog Selection' }),
      className: 'btn btn-success',
      onClick: this.preparedRowSelection?.length ? this.onSaveReportAction : this.onSaveCatalogSelection
    }, {
      label: 'GA.BUTTONS.CANCEL',
      className: 'btn btn-danger',
      onClick: () => this.onCancel.emit()
    }];
  }

  private transformModelToRequestData(): Partial<ICatalogSelectionRequest> {
    return {
      includeInWebshopCatalog: this.model.includeInWebshopCatalog,
      categorySelection: {
        wildcard: !!(this.model.selectAllCategories && this.model.includeInWebshopCatalog),
        categories: this.model.includeInWebshopCatalog && !this.model.selectAllCategories ? _.map(this.model.productCategories || [], (category: IProductCategory) => category.name) : []
      }
    };
  }

  private onSaveReportAction = (): void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.onSaved.emit(this.transformModelToRequestData());
      });
  };

  private onSaveCatalogSelection = (): void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.setSaveBusy(true);

        this.requestData = _.extend({
          countryId: this.countryId,
          companyCountryId: this.companyCountryId,
          oemId: this.oemId
        }, this.transformModelToRequestData());

        this.webshopCatalogSelectionDataService.saveCategoriesSelection(this.webshopId, this.requestData)
          .then(() => {
            // emit request data and trigger onSaved event
            this.onSaved.emit(this.requestData);
          })
          .finally(() => {
            this.setSaveBusy(false);
          });
      });
  };

  private onChangeProductCategories = (): void => {
    if (this.selectAllCategoriesCheckField?.formControl) {
      this.selectAllCategoriesCheckField.formControl.updateValueAndValidity();
    }
  };

  private setSaveBusy(busy: boolean): void {
    const button: IDialogButton = _.find(this.buttons, { id: BUTTON_SAVE });
    button.busy = busy;
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-4';
    const valueClass:string = 'col-sm-8';

    return [
      this.formlyService.aGroup()
        .withLabel('Include in webshop catalog')
        .withFields([
          this.formlyService.aCheckbox('includeInWebshopCatalog')
            .withoutWrapper()
            .withValueClass('col-sm-12')
            .onChange(this.onIncludeSelectionChange)
            .build()
        ])
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.productCategoriesField = this.formlyService.aMultiSelect('productCategories', 'Product categories')
        .withFormat(this.formattingService.formatDescription)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.onChangeProductCategories)
        .withExtraOptions({
          templateOptions: {
            localSearch: true,
            trackBy: 'name'
          },
          validators: {
            [VALIDATION_KEY]: {
              expression: (control: AbstractControl): boolean => {
                if (this.model.includeInWebshopCatalog) {
                  return (!!control.value?.length || this.model.selectAllCategories);
                }

                return true;
              },
              message: (): string => VALIDATION_MESSAGE
            }
          }
        })
        .build(),
      this.selectAllCategoriesGroupField = this.formlyService.aGroup()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFields([
          this.selectAllCategoriesCheckField = this.formlyService.aCheckbox('selectAllCategories', 'Select all product categories')
            .withoutWrapper()
            .withValueClass('col-sm-12')
            .onChange(this.onSelectAllCategoriesChange)
            .withExtraOptions({
              validators: {
                [VALIDATION_KEY]: {
                  expression: (control: AbstractControl): boolean => {
                    if (this.model.includeInWebshopCatalog) {
                      return (control.value || !!this.model.productCategories?.length);
                    }

                    return true;
                  }
                }
              }
            })
            .build()
        ])
        .build()
    ];
  }

  private initModelByRowSelection(): void {
    const includedValues: string[] = _.pluck(this.preparedRowSelection, 'Include_in_shop_catalog');
    const diffIncluded: string[] = _.uniq(includedValues);

    if (diffIncluded.length === 1) {
      this.model.includeInWebshopCatalog = diffIncluded[0]?.toLowerCase() === 'yes';
    } else {
      this.model.includeInWebshopCatalog = false;
    }

    // Product categories selection
    const categoriesSelectionValues: string[] = _.pluck(this.preparedRowSelection, 'Categories_selection');
    const diffCategoriesSelection: string[] = _.uniq(categoriesSelectionValues);

    if (diffCategoriesSelection.length === 1) {
      this.model.selectAllCategories = diffCategoriesSelection[0] === ALL_PRODUCT_CATEGORIES;
      this.productCategories = diffCategoriesSelection[0];
    } else {
      this.model.selectAllCategories = false;
      this.productCategories = undefined;
    }
  }
}